import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Avatar,
    Divider,
    CircularProgress,
    makeStyles,
} from "@material-ui/core";
import React from 'react'
import { CommentReport, PostReport, ProfileReport } from './FlaggedContent';
import defaultAvatars from "../../../../../assets/defaultAvatars.svg";
import { Link } from "react-router-dom";

export const useStyles = makeStyles(theme => ({
    contentCard: {
        backgroundColor: "#1a1a1a",
        border: "1px solid #333",
        marginBottom: theme.spacing(2),
        color: "#fff",
        "&:hover": {
            borderColor: "#FE5A97",
        },
    },
    contentHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(1),
    },
    userInfo: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    contentPreview: {
        maxWidth: "200px",
        maxHeight: "200px",
        borderRadius: "8px",
        objectFit: "cover",
    },
    contentPreviewContainer: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#2a2a2a",
        borderRadius: "8px",
        border: "1px solid #444",
    },
    contentPreviewImage: {
        maxWidth: "100%",
        maxHeight: "400px",
        borderRadius: "8px",
        objectFit: "contain",
        marginBottom: theme.spacing(1),
    },
    contentPreviewVideo: {
        maxWidth: "100%",
        maxHeight: "400px",
        borderRadius: "8px",
        marginBottom: theme.spacing(1),
    },
    contentPreviewText: {
        padding: theme.spacing(1.5),
        backgroundColor: "#1a1a1a",
        borderRadius: "4px",
        border: "1px solid #333",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    postStatusBadge: {
        marginLeft: theme.spacing(1),
    },
    expandButton: {
        marginTop: theme.spacing(1),
        color: "#FE5A97",
        textTransform: "none",
    },
    actionButtons: {
        display: "flex",
        gap: theme.spacing(1),
    },
    approveButton: {
        backgroundColor: "#4caf50",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#45a049",
        },
    },
    rejectButton: {
        backgroundColor: "#f44336",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#da190b",
        },
    },
}))

type ReportCardProps = {
    report: ProfileReport,
    reportType: "post" | "profile" | "comment" | null,
    loadingAvatars: {
        [key: string]: boolean;
    },
    avatarUrls: { [key: string]: string },
    mediaUrls: { [key: string]: string },
    loadingMedia: {
        [key: string]: boolean
    },
    fetchMediaUrl: (mediaUrl: string, key: string) => void,
    setReportStatusDialog: React.Dispatch<React.SetStateAction<{
        open: boolean;
        report: PostReport | ProfileReport | CommentReport | null;
        status: string;
        moderatorComment?: string;
        reportType?: "post" | "profile" | "comment" | null;
    }>>,
    loadProfileReports: () => Promise<void>
}

export default function ReportCard({ report, reportType, loadingAvatars, avatarUrls, mediaUrls, loadingMedia, fetchMediaUrl, setReportStatusDialog, loadProfileReports }: ReportCardProps) {
    const classes = useStyles();

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <Card key={report._id} className={classes.contentCard}>
            <CardContent>
                <Box className={classes.contentHeader}>
                    <Box className={classes.userInfo}>
                        {loadingAvatars[report.profile?._id || ""] ? (
                            <Avatar>
                                <CircularProgress size={24} />
                            </Avatar>
                        ) : (
                            <Avatar
                                src={
                                    report.profile?._id &&
                                        avatarUrls[report.profile._id]
                                        ? avatarUrls[report.profile._id]
                                        : report.profile?.profileImg
                                            ? report.profile.profileImg
                                            : defaultAvatars
                                }
                                alt={report.profile?.username || "User"}
                            >
                                {!report.profile?.profileImg &&
                                    !avatarUrls[report.profile?._id || ""] &&
                                    (report.profile?.firstName?.[0] ||
                                        report.profile?.username?.[0] ||
                                        "U")}
                            </Avatar>
                        )}
                        <Box>
                            <Typography variant="h6" style={{ color: "#fff" }}>
                                {report.profile?.username ? (
                                    <Link
                                        to={`/${report.profile.username}`}
                                        style={{
                                            color: "#FE5A97",
                                            textDecoration: "none",
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.textDecoration =
                                                "underline";
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.textDecoration = "none";
                                        }}
                                    >
                                        {report.profile.username}
                                    </Link>
                                ) : (
                                    report.profile?.firstName || "Unknown User"
                                )}
                            </Typography>
                            <Typography variant="body2" style={{ color: "#ccc" }}>
                                {report.profile?.email || "No email available"}
                            </Typography>
                        </Box>
                    </Box>
                    {report.reporter && (
                        <Box
                            className={classes.userInfo}
                            style={{ marginTop: "12px", marginBottom: "8px" }}
                        >
                            {loadingAvatars[report.reporter._id] ? (
                                <Avatar>
                                    <CircularProgress size={24} />
                                </Avatar>
                            ) : (
                                <Avatar
                                    src={
                                        report.reporter._id &&
                                            avatarUrls[report.reporter._id]
                                            ? avatarUrls[report.reporter._id]
                                            : report.reporter.profileImg
                                                ? report.reporter.profileImg
                                                : defaultAvatars
                                    }
                                    alt={report.reporter.username || "Reporter"}
                                    style={{ width: 32, height: 32 }}
                                >
                                    {!report.reporter.profileImg &&
                                        !avatarUrls[report.reporter._id] &&
                                        (report.reporter.firstName?.[0] ||
                                            report.reporter.username?.[0] ||
                                            "R")}
                                </Avatar>
                            )}
                            <Box>
                                <Typography
                                    variant="body2"
                                    style={{ color: "#999", fontSize: "12px" }}
                                >
                                    Reported by:
                                </Typography>
                                <Typography variant="body2" style={{ color: "#fff" }}>
                                    {report.reporter.username ? (
                                        <Link
                                            to={`/u/${report.reporter.username}`}
                                            style={{
                                                color: "#FE5A97",
                                                textDecoration: "none",
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.textDecoration =
                                                    "underline";
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.textDecoration = "none";
                                            }}
                                        >
                                            {report.reporter.username}
                                        </Link>
                                    ) : (
                                        report.reporter.firstName || "Unknown"
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: "8px" }}
                    >
                        <Chip
                            label={
                                report.status === "userDeleted"
                                    ? "PROFILE BANNED"
                                    : report.status.toUpperCase()
                            }
                            variant="outlined"
                            size="small"
                            style={{
                                color: "#fff",
                                borderColor:
                                    report.status === "open"
                                        ? "#4caf50"
                                        : report.status === "pending"
                                            ? "#ff9800"
                                            : report.status === "reviewing"
                                                ? "#ff9800"
                                                : report.status === "resolved"
                                                    ? "#2196f3"
                                                    : report.status === "userDeleted"
                                                        ? "#f44336"
                                                        : "#999",
                                backgroundColor:
                                    report.status === "userDeleted"
                                        ? "rgba(244, 67, 54, 0.1)"
                                        : "transparent",
                            }}
                        />

                    </Box>
                </Box>

                <Divider
                    style={{ backgroundColor: "#333", margin: "16px 0" }}
                />

                <Box>
                    <Typography
                        variant="body1"
                        style={{ marginBottom: "8px", color: "#fff" }}
                    >
                        <strong>User ID:</strong>
                        {report.profile && report.profile.username ?
                            <Link
                                to={`/${report.profile.username}`}
                                style={{
                                    color: "#FE5A97",
                                    textDecoration: "none",
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.textDecoration =
                                        "underline";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.textDecoration = "none";
                                }}
                            >
                                {report.profile?._id}
                            </Link>
                            : report.profile?._id}
                    </Typography>
                    <Typography
                        variant="body1"
                        style={{ marginBottom: "8px", color: "#fff" }}
                    >
                        <strong>Reason:</strong> {report.reason}
                    </Typography>
                    {report.comment && (
                        <Typography
                            variant="body2"
                            style={{ marginBottom: "8px", color: "#fff" }}
                        >
                            <strong>Comment:</strong> {report.comment}
                        </Typography>
                    )}


                    {(report.aiFlags && report.aiFlags.length > 0) && (
                        <Typography
                            variant="body2"
                            style={{ marginBottom: "8px", color: "#fff" }}
                        >
                            <strong>AI Flags:</strong>
                            <ul>
                                {report.aiFlags.map(flag => (
                                    <li key={flag._id}>
                                        <strong>Field:</strong> {flag.field}<br />
                                        <strong>Severity:</strong> {flag.severity}<br />
                                        <strong>Provider:</strong> {flag.provider}<br />
                                        <strong>Original Text:</strong> {flag.originalText}<br />
                                        <strong>Matched Terms:</strong> {flag.matchedTerms.join(", ")}
                                    </li>
                                ))}
                            </ul>
                        </Typography>
                    )}

                    <Typography variant="body2" style={{ color: "#ccc" }}>
                        <strong>Reported:</strong> {formatDate(report.createdAt)}
                    </Typography>
                </Box>

                <Box
                    className={classes.actionButtons}
                    style={{ marginTop: "16px" }}
                >
                    <Button
                        variant="contained"
                        className={classes.approveButton}
                        onClick={() =>
                            setReportStatusDialog({
                                open: true,
                                report,
                                status: report.status || "open",
                                moderatorComment: "",
                                reportType
                            })
                        }
                    >
                        Change Status
                    </Button>
                </Box>
            </CardContent>
        </Card>
    )
}
